import { featureFlagClient } from './feature-flag-client';
import { Analytics } from '@trello/atlassian-analytics';

const FX3_SOAK_FLAG = 'fep.fx3-soaking-flag';
const FX3_CONSISTENCY_CHECK_FLAG = 'fep.fx3-check-consistency';
const FX3_CONSISTENCY_THRESHOLD_MS = 'fep.fx3-consistency-threshold-time-ms';

class Fx3ConsistencyChecker {
  private consistencyCheckTimeout: number | null = null;

  private getConsistencyThreshold = (): number => {
    // Get the consistency threshold time, defaulting to fx3's polling time
    return featureFlagClient.client.variation(
      FX3_CONSISTENCY_THRESHOLD_MS,
      featureFlagClient.pollingInterval,
    );
  };

  private compareValuesAndReport = (isStartup: boolean) => {
    // This is the kill switch for measuring consistency of the clients
    if (
      !featureFlagClient.client.variation(FX3_CONSISTENCY_CHECK_FLAG, false)
    ) {
      return;
    }

    // Grab the value directly from the ldclient, and attempt to do the same for the fx3 client
    const ldValue = featureFlagClient.client.variation(FX3_SOAK_FLAG, false);
    let fx3Value: boolean | null = null;
    try {
      fx3Value = featureFlagClient.atlassianClient.getFlagValue(
        FX3_SOAK_FLAG,
        false,
      );
    } catch (error) {
      Analytics.sendOperationalEvent({
        action: 'errored',
        actionSubject: 'featureFlag',
        source: 'lib:featureFlagWebClient',
        attributes: {
          errorMessage: error.message,
        },
      });
    }

    // Determine the evaluationResult to send to GAS
    let evaluationResult: string | null = null;
    if (fx3Value === null) {
      evaluationResult = 'error';
    } else if (fx3Value !== ldValue) {
      evaluationResult = 'mismatch';
    } else {
      evaluationResult = 'match';
    }

    Analytics.sendOperationalEvent({
      action: 'evaluated',
      actionSubject: 'featureFlag',
      source: 'lib:featureFlagWebClient',
      attributes: {
        consistencyThreshold: this.getConsistencyThreshold(),
        evaluationType: isStartup ? 'startup' : 'change',
        evaluationResult,
      },
    });
  };

  public initialize() {
    // Compare at startup
    this.compareValuesAndReport(true);

    // Compare values at runtime, checking consistency _after_ some configurable period of time,
    // to account for fx3's polling based approach.
    featureFlagClient.client.on(`change:${FX3_SOAK_FLAG}`, (newValue) => {
      // If this update happened before a scheduled comparison, cancel it
      if (this.consistencyCheckTimeout !== null) {
        window.clearTimeout(this.consistencyCheckTimeout);
        this.consistencyCheckTimeout = null;
      }

      // Wait for the eventual consistency threshold before comparing the values
      this.consistencyCheckTimeout = window.setTimeout(() => {
        this.compareValuesAndReport(false);
        this.consistencyCheckTimeout = null;
      }, this.getConsistencyThreshold());
    });
  }
}

export const fx3ConsistencyChecker = new Fx3ConsistencyChecker();
